import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Box, TextField, useQuickCreateMenuPaper } from '@/shared/ui';
import { usePermission } from '@/shared/hooks/usePermission';
import { queryKeys } from '@/shared/query';
import {
  appointmentTypeAPI,
  type AppointmentType,
} from '@/modules/appointment/api/appointmentType.api';
import QuickCreateAppointmentTypeDialog from './QuickCreateAppointmentTypeDialog';

interface AppointmentTypeSelectProps {
  value: string;
  onChange: (typeId: string, type: AppointmentType | null) => void;
  disabled?: boolean;
  error?: string | null;
  /** Include inactive types when editing an appointment that already uses one. */
  includeInactiveIds?: string[];
}

const typeLabel = (type: AppointmentType, inactiveLabel: string): string =>
  type.isActive ? type.name : `${type.name} (${inactiveLabel})`;

const selectedAutocompleteSx = {
  '&:not(:hover):not(.Mui-focused) .MuiAutocomplete-endAdornment': {
    display: 'none',
  },
  '&:not(:hover):not(.Mui-focused) .MuiOutlinedInput-root': {
    pr: '8px !important',
  },
};

const AppointmentTypeSelect: React.FC<AppointmentTypeSelectProps> = ({
  value,
  onChange,
  disabled = false,
  error,
  includeInactiveIds = [],
}) => {
  const { t } = useTranslation('appointment');
  const { hasPermission } = usePermission();
  const canCreate = hasPermission('appointment-types:create') && !disabled;
  const [createOpen, setCreateOpen] = useState(false);
  const [pinnedType, setPinnedType] = useState<AppointmentType | null>(null);
  const needsInactive = includeInactiveIds.length > 0;
  const inactiveLabel = t('catalog.status.inactive');

  const typesQuery = useQuery({
    queryKey: queryKeys.appointment.types({ includeInactive: needsInactive }),
    queryFn: async () => {
      const response = await appointmentTypeAPI.getAll(needsInactive);
      return response.data ?? [];
    },
  });

  const types = useMemo(() => {
    const all = typesQuery.data ?? [];
    const active = all.filter((item) => item.isActive);
    const extras = all.filter(
      (item) => !item.isActive && includeInactiveIds.includes(item.id)
    );
    const byId = new Map<string, AppointmentType>();
    [...active, ...extras, ...(pinnedType ? [pinnedType] : [])].forEach((item) =>
      byId.set(item.id, item)
    );
    return Array.from(byId.values()).sort((a, b) =>
      a.name.localeCompare(b.name, 'vi')
    );
  }, [typesQuery.data, pinnedType, includeInactiveIds]);

  const selected = types.find((item) => item.id === value) ?? null;
  const typeQuickCreate = useQuickCreateMenuPaper({
    canCreate,
    createLabel: t('fields.createNewType'),
    onQuickCreate: () => setCreateOpen(true),
  });

  return (
    <>
      <Autocomplete
        id="appointmentType"
        fullWidth
        forcePopupIcon={selected ? false : 'auto'}
        sx={selected ? selectedAutocompleteSx : undefined}
        options={types}
        loading={typesQuery.isPending}
        disabled={disabled}
        value={selected}
        slots={{ paper: typeQuickCreate.makeAutocompletePaper }}
        getOptionLabel={(option) => typeLabel(option, inactiveLabel)}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        onChange={(_event, next) => {
          onChange(next?.id ?? '', next);
        }}
        renderOption={(props, option) => {
          const { key, ...rest } = props as typeof props & { key?: React.Key };
          return (
            <Box
              component="li"
              key={key}
              {...rest}
              sx={{
                '&&': {
                  alignItems: 'flex-start',
                  height: 'auto',
                  whiteSpace: 'normal',
                },
              }}
            >
              {option.color ? (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: option.color,
                    mr: 1,
                    mt: '0.35em',
                    flexShrink: 0,
                  }}
                />
              ) : null}
              {typeLabel(option, inactiveLabel)}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('fields.type')}
            placeholder={t('fields.typePlaceholder')}
            required
            error={Boolean(error)}
            helperText={error ?? undefined}
            disabled={disabled}
          />
        )}
      />
      <QuickCreateAppointmentTypeDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(type) => {
          setPinnedType(type);
          setCreateOpen(false);
          onChange(type.id, type);
        }}
      />
    </>
  );
};

export default AppointmentTypeSelect;
